import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { opportunities, proposals, sam, ingestion, admin } from "@/lib/api";
import { useAuth } from "@/context/AuthProvider";

const MANIFEST_PILL = {
  indexed: "pill-green",
  ocr: "pill-green",
  expanded: "pill-blue",
  empty: "pill-gray",
  pending: "pill-gray",
  queued: "pill-gray",
  needs_ocr: "pill-orange",
  encrypted: "pill-red",
  unsupported: "pill-gray",
  failed: "pill-red",
};

function urgencyPill(o) {
  if (o.status === "submitted") return <span className="pill pill-green">SUBMITTED</span>;
  if (o.status === "draft") return <span className="pill pill-blue">DRAFT</span>;
  if (o.status === "in_review") return <span className="pill pill-purple">IN REVIEW</span>;
  if (o.urgent) return <span className="pill pill-red">URGENT</span>;
  if (o.status === "new") return <span className="pill pill-gray">NEW MATCH</span>;
  return null;
}

function SamUsageGauge({ usage }) {
  const { calls_today = 0, daily_quota = 1000, remaining = 1000, role, resets_at } = usage || {};
  const pct = Math.min(100, Math.round((calls_today * 100) / Math.max(1, daily_quota)));
  let color = "var(--success)";
  let cls = "pill-green";
  if (pct >= 90) { color = "var(--danger)"; cls = "pill-red"; }
  else if (pct >= 70) { color = "var(--warning)"; cls = "pill-orange"; }
  const tooltip = `Role: ${role || "unknown"} · ${calls_today}/${daily_quota} calls today · ${remaining} remaining${resets_at ? ` · resets ${resets_at}` : ""}`;
  return (
    <span
      className={`pill ${cls}`}
      title={tooltip}
      data-testid="sam-usage-gauge"
      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
    >
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {calls_today} / {daily_quota}
      </span>
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 60,
          height: 5,
          borderRadius: 10,
          background: "rgba(0,0,0,0.08)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <span
          style={{
            position: "absolute",
            inset: 0,
            width: `${pct}%`,
            background: color,
            borderRadius: 10,
            transition: "width 0.4s ease",
          }}
        />
      </span>
      <span style={{ fontSize: 10, opacity: 0.75 }}>{role || "SAM.gov"}</span>
    </span>
  );
}

function JobFiles({ files }) {
  return (
    <div style={{ marginTop: 12, textAlign: "left", maxWidth: 520 }} data-testid="upload-manifest" onClick={(e) => e.stopPropagation()}>
      {files.map((f, i) => (
        <div key={i} className="flex items-center gap-8" style={{ fontSize: 12, padding: "3px 0" }}>
          <span className={`pill ${MANIFEST_PILL[f.status] || "pill-gray"}`} style={{ minWidth: 84, textAlign: "center" }}>{f.status}</span>
          <span style={{ fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
        </div>
      ))}
    </div>
  );
}

export default function Opportunities() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [opps, setOpps] = useState([]);
  const [openingId, setOpeningId] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [matchesOnly, setMatchesOnly] = useState(false);
  const [agency, setAgency] = useState("");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadJob, setUploadJob] = useState(null);
  const [pwPrompt, setPwPrompt] = useState(null); // { proposalId, file }
  const [pwValue, setPwValue] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // SAM.gov state
  const [samStatus, setSamStatus] = useState(null);
  const [samSyncing, setSamSyncing] = useState(false);
  const [samError, setSamError] = useState("");
  const [samResult, setSamResult] = useState(null);
  const [samFilters, setSamFilters] = useState({
    naics: "541512",
    ptype: "",
    days_back: 14,
    limit: 25,
  });
  const [samPanelOpen, setSamPanelOpen] = useState(false);
  const [shreddingId, setShreddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const load = async () => {
    setOpps(await opportunities.list());
  };
  // Always open THIS opportunity's proposal — creating one linked to it if none
  // exists yet — so we never fall back to an arbitrary most-recent proposal.
  const openProposalFor = async (o) => {
    if (openingId) return;
    setOpeningId(o.id);
    try {
      const { proposal_id } = await opportunities.respond(o.id);
      navigate(`/workspace?proposal=${proposal_id}`);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not open this response");
    } finally {
      setOpeningId(null);
    }
  };
  const loadStatus = async () => {
    try { setSamStatus(await sam.status()); } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadStatus(); }, []);

  const counts = {
    ALL: opps.length,
    RFP: opps.filter((o) => o.rfp_type === "RFP").length,
    RFI: opps.filter((o) => o.rfp_type === "RFI").length,
    RFQ: opps.filter((o) => o.rfp_type === "RFQ").length,
  };

  const matchCount = opps.filter((o) => (o.p_win || 0) >= 60).length;

  const filtered = opps.filter((o) => {
    if (matchesOnly && (o.p_win || 0) < 60) return false;
    if (filter !== "ALL" && o.rfp_type !== filter) return false;
    if (agency && !(o.agency || "").toLowerCase().includes(agency.toLowerCase())) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !(o.title || "").toLowerCase().includes(q) &&
        !(o.agency || "").toLowerCase().includes(q) &&
        !(o.naics || "").toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const pollJob = async (jobId) => {
    for (let i = 0; i < 90; i++) {
      const job = await ingestion.job(jobId);
      setUploadJob(job);
      if (job.status === "done" || job.status === "error") return job;
      await new Promise((r) => setTimeout(r, 2000));
    }
    return null;
  };

  const onFile = async (file) => {
    setUploadError("");
    setUploadResult(null);
    setUploadJob(null);
    setPwPrompt(null);
    setUploading(true);
    try {
      const res = await opportunities.upload(file); // { proposal_id, job_id, status }
      setUploadResult(res);
      const job = await pollJob(res.job_id);
      const encrypted = (job?.files || []).filter((f) => f.status === "encrypted");
      if (encrypted.length) setPwPrompt({ proposalId: res.proposal_id, file });
      await load();
    } catch (e) {
      setUploadError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Retry an encrypted file by attaching it to the already-created proposal with a password.
  const unlockFile = async () => {
    if (!pwPrompt) return;
    setUploading(true);
    try {
      const { job_id } = await proposals.addDocuments(pwPrompt.proposalId, [pwPrompt.file], { "*": pwValue });
      await pollJob(job_id);
      setPwPrompt(null);
      setPwValue("");
      await load();
    } catch (e) {
      setUploadError(e?.response?.data?.detail || "Unlock failed");
    } finally {
      setUploading(false);
    }
  };

  const runSamSync = async () => {
    setSamError("");
    setSamResult(null);
    setSamSyncing(true);
    try {
      const res = await sam.sync(samFilters);
      setSamResult(res);
      await load();
      await loadStatus();
    } catch (e) {
      setSamError(e?.response?.data?.detail || "SAM.gov sync failed");
      // Refresh the gauge even on failure so user sees the 429-related last_status
      try { await loadStatus(); } catch { /* ignore */ }
    } finally {
      setSamSyncing(false);
    }
  };

  const shredOpp = async (id) => {
    setShreddingId(id);
    try {
      await opportunities.shred(id);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Shred failed");
    } finally {
      setShreddingId(null);
    }
  };

  const removeOpp = async (o) => {
    if (!window.confirm(`Remove "${o.title}" from Opportunities? This can't be undone.`)) return;
    setRemovingId(o.id);
    try {
      await admin.deleteOpportunity(o.id);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not remove this opportunity");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="page" data-testid="page-opportunities">
      <div className="page-header">
        <div>
          <div className="page-title">Opportunities</div>
          <div className="page-subtitle">
            Browse and filter solicitations · {opps.length} active matches
          </div>
        </div>
        <div className="flex gap-8">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/new-response")}
            data-testid="new-response-btn"
          >
            ＋ New Response
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => setSamPanelOpen((v) => !v)}
            data-testid="sam-sync-btn"
          >
            🌐 Sync from SAM.gov
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => fileRef.current?.click()}
            data-testid="upload-zip-btn"
          >
            📥 Import ZIP
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => fileRef.current?.click()}
            data-testid="upload-solicitation-btn"
          >
            ⬆ Upload Solicitation
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.doc,.txt,.xlsx,.zip"
            style={{ display: "none" }}
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
            data-testid="upload-file-input"
          />
        </div>
      </div>

      {samPanelOpen && (
        <div className="card" data-testid="sam-panel">
          <div className="card-header">
            <span className="card-title">🌐 SAM.gov Live Sync</span>
            <div className="flex gap-8 items-center">
              {samStatus?.usage && !samStatus.mock_mode && (
                <SamUsageGauge usage={samStatus.usage} />
              )}
              {samStatus?.mock_mode && (
                <span className="pill pill-gray" data-testid="sam-mock-pill" title="SAM.gov API key not validated — using sample data. Set SAM_MOCK=false and provide a valid SAM_API_KEY to enable live sync.">
                  🧪 MOCK MODE
                </span>
              )}
              <span className={`pill ${samStatus?.configured ? "pill-green" : "pill-orange"}`}>
                {samStatus?.configured ? "API Key Configured" : "Not Configured"}
              </span>
            </div>
          </div>
          <div style={{ padding: "14px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, alignItems: "end" }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>NAICS</label>
              <input
                className="select-filter"
                style={{ width: "100%" }}
                placeholder="541512"
                value={samFilters.naics}
                onChange={(e) => setSamFilters({ ...samFilters, naics: e.target.value })}
                data-testid="sam-naics-input"
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Notice Type</label>
              <select
                className="select-filter"
                style={{ width: "100%" }}
                value={samFilters.ptype}
                onChange={(e) => setSamFilters({ ...samFilters, ptype: e.target.value })}
                data-testid="sam-ptype-select"
              >
                <option value="">All</option>
                <option value="o">Solicitation</option>
                <option value="p">Pre-solicitation</option>
                <option value="k">Combined Synopsis</option>
                <option value="r">Sources Sought</option>
                <option value="s">Special Notice</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Days Back</label>
              <input
                type="number" min="1" max="60"
                className="select-filter"
                style={{ width: "100%" }}
                value={samFilters.days_back}
                onChange={(e) => setSamFilters({ ...samFilters, days_back: Number(e.target.value) || 14 })}
                data-testid="sam-days-input"
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Max Results</label>
              <input
                type="number" min="1" max="200"
                className="select-filter"
                style={{ width: "100%" }}
                value={samFilters.limit}
                onChange={(e) => setSamFilters({ ...samFilters, limit: Number(e.target.value) || 25 })}
                data-testid="sam-limit-input"
              />
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={runSamSync}
              disabled={samSyncing}
              data-testid="sam-run-sync-btn"
              style={{ height: 38 }}
            >
              {samSyncing ? <><span className="spinner white" /> Syncing…</> : "Run Sync"}
            </button>
          </div>
          <div style={{ padding: "0 20px 14px", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "var(--text-secondary)" }}>
            {samStatus && (
              <span>
                Imported total: <strong>{samStatus.total_imported}</strong> · Sync mode:{" "}
                <strong>Manual only</strong>
                {samStatus.last_sync && (
                  <> · Last sync: <strong>{new Date(samStatus.last_sync.finished_at).toLocaleString()}</strong>{" "}
                    (+{samStatus.last_sync.inserted} new, {samStatus.last_sync.updated} updated)
                  </>
                )}
              </span>
            )}
          </div>
          {samError && (
            <div className="alert alert-danger" style={{ margin: "0 20px 14px" }} data-testid="sam-error">
              <span>⚠️</span><span>{samError}</span>
            </div>
          )}
          {samResult && (
            <div className={`alert ${samResult.mock ? "alert-info" : "alert-success"}`} style={{ margin: "0 20px 14px" }} data-testid="sam-result">
              <span>{samResult.mock ? "🧪" : "✅"}</span>
              <span>
                {samResult.mock && <strong>MOCK · </strong>}
                Fetched {samResult.fetched} opportunities (of {samResult.total_available} available) ·{" "}
                <strong>{samResult.inserted}</strong> new · <strong>{samResult.updated}</strong> updated
                {samResult.fallback_reason && <em> · {samResult.fallback_reason}</em>}
              </span>
            </div>
          )}
        </div>
      )}

      <div
        className={`dropzone ${dragOver ? "dragging" : ""}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.[0]) onFile(e.dataTransfer.files[0]);
        }}
        data-testid="upload-dropzone"
      >
        {uploading || (uploadJob && uploadJob.status === "processing") ? (
          <>
            <div className="dz-icon"><span className="spinner lg" /></div>
            <div className="dz-title">Ingesting & shredding…</div>
            <div className="dz-sub">Parsing (OCR if scanned) → extracting requirements with Claude</div>
            {uploadJob?.files?.length > 0 && <JobFiles files={uploadJob.files} />}
          </>
        ) : uploadJob && uploadJob.status === "done" ? (
          <>
            <div className="dz-icon">✅</div>
            <div className="dz-title">Ingestion complete</div>
            <div className="dz-sub">
              {uploadJob.summary} ·{" "}
              <a
                style={{ color: "var(--primary)", fontWeight: 600 }}
                onClick={(e) => { e.stopPropagation(); navigate("/workspace?proposal=" + uploadResult.proposal_id); }}
              >
                Open response workspace →
              </a>
            </div>
            {uploadJob.files?.length > 0 && <JobFiles files={uploadJob.files} />}
            {pwPrompt && (
              <div className="alert alert-warn" style={{ marginTop: 12, maxWidth: 520 }} onClick={(e) => e.stopPropagation()} data-testid="pw-prompt">
                <span>🔒</span>
                <span style={{ flex: 1 }}>
                  A file is password-protected. Enter the password to decrypt and ingest it:
                  <div className="flex gap-8" style={{ marginTop: 8 }}>
                    <input type="password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="Document password" style={{ flex: 1 }} data-testid="pw-input" />
                    <button className="btn btn-primary btn-sm" onClick={unlockFile} disabled={!pwValue}>Unlock</button>
                  </div>
                </span>
              </div>
            )}
          </>
        ) : uploadJob && uploadJob.status === "error" ? (
          <>
            <div className="dz-icon">⚠️</div>
            <div className="dz-title">Ingestion failed</div>
            <div className="dz-sub">{uploadJob.summary || "Could not process the upload."}</div>
          </>
        ) : (
          <>
            <div className="dz-icon">📥</div>
            <div className="dz-title">Drop solicitation here — or click to upload</div>
            <div className="dz-sub">PDF (incl. scanned), DOCX, XLSX, MSG, TXT or ZIP · runs in the background</div>
          </>
        )}
        {uploadError && (
          <div className="err" style={{ color: "var(--danger)", marginTop: 10, fontSize: 12 }}>
            {uploadError}
          </div>
        )}
      </div>

      <div className="alert alert-info" data-testid="ai-match-banner">
        <span>🤖</span>
        <span>
          <strong>AI Matching:</strong> {matchCount} opportunities match your company's
          capability profile.{" "}
          {matchCount > 0 && (
            <a
              onClick={() => setMatchesOnly((v) => !v)}
              style={{ color: "var(--primary)", cursor: "pointer", fontWeight: 600 }}
              data-testid="view-matches"
            >
              {matchesOnly ? "← Show all" : "View matches →"}
            </a>
          )}
        </span>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <span className="search-icon">🔍</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, agency, NAICS, or keyword…"
            data-testid="search-input"
          />
        </div>
        {["ALL", "RFP", "RFI", "RFQ"].map((k) => (
          <div
            key={k}
            className={`filter-chip ${filter === k ? "active" : ""}`}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k.toLowerCase()}`}
          >
            {k === "ALL" ? "All" : k} ({counts[k]})
          </div>
        ))}
        <select className="select-filter" value={agency} onChange={(e) => setAgency(e.target.value)}>
          <option value="">All Agencies</option>
          <option>Defense</option>
          <option>Homeland</option>
          <option>GSA</option>
          <option>Veterans</option>
          <option>Army</option>
        </select>
      </div>

      <div className="opp-cards">
        {filtered.map((o) => {
          return (
            <div className="opp-card" key={o.id} data-testid={`opp-card-${o.id}`} onClick={() => openProposalFor(o)}>
              <div className="opp-card-top">
                <div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                    <span className={`opp-type ${(o.rfp_type || "rfp").toLowerCase()}`}>{o.rfp_type}</span>
                    {urgencyPill(o)}
                    {o.source === "sam.gov" && (
                      <span className="pill pill-blue" data-testid={`sam-badge-${o.id}`}>🌐 SAM.gov</span>
                    )}
                    {o.is_mock && (
                      <span className="pill pill-gray" title="Sample data — provide a valid SAM.gov API key to import live opportunities">🧪 MOCKED</span>
                    )}
                    {o.auto_shredded && (
                      <span className="pill pill-purple">🤖 Shredded</span>
                    )}
                  </div>
                  <div className="opp-card-title">{o.title}</div>
                  <div className="opp-card-agency">{o.agency}</div>
                </div>
              </div>
              <div className="opp-card-meta">
                {o.due_date && (
                  <span className="opp-card-tag">
                    📅 Due {new Date(o.due_date).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
                {o.naics && <span className="opp-card-tag">🏷 NAICS {o.naics}</span>}
                {o.value_range && <span className="opp-card-tag">💰 {o.value_range}</span>}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                {o.description}
              </div>
              <div className="opp-card-footer">
                <div className="flex gap-8">
                  {o.ui_link && (
                    <a
                      className="btn btn-ghost btn-sm"
                      href={o.ui_link}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ↗ SAM.gov
                    </a>
                  )}
                  {!o.auto_shredded && o.source === "sam.gov" && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={(e) => { e.stopPropagation(); shredOpp(o.id); }}
                      disabled={shreddingId === o.id}
                      data-testid={`shred-btn-${o.id}`}
                    >
                      {shreddingId === o.id ? <><span className="spinner" /> Shredding…</> : "🤖 Shred"}
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={(e) => { e.stopPropagation(); openProposalFor(o); }}
                    disabled={openingId === o.id}
                    data-testid={`respond-btn-${o.id}`}
                  >
                    {openingId === o.id ? <><span className="spinner" /> Opening…</> : "Respond"}
                  </button>
                  {hasPerm("user.manage") && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: "var(--danger)" }}
                      onClick={(e) => { e.stopPropagation(); removeOpp(o); }}
                      disabled={removingId === o.id}
                      title="Remove this opportunity — it won't appear in the list anymore"
                      data-testid={`remove-opp-btn-${o.id}`}
                    >
                      {removingId === o.id ? <><span className="spinner" /> Removing…</> : "✕ Remove"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
